import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    ApartmentOutlined,
    BulbOutlined,
    CheckCircleFilled,
    FileSearchOutlined,
    HighlightOutlined,
    ProfileOutlined,
} from '@ant-design/icons';
import { message, Modal, Progress, theme } from 'antd';
import classNames from 'classnames';
import { createOrder, generateOutline as requestOutline, MODEL, rememberOrder } from '../../api';
import {
    buildCustomRef,
    contentsToLightTypes,
    dataSearchLightTypes,
    errorText,
    isErrorNotified,
    MAX_REFERENCE_COUNT,
    toEditableOutline,
    toSubmitOutlines,
} from '../utils';
import EditChapterModal from './EditChapterModal';
import EngineSwitchNotice from './EngineSwitchNotice';
import OutlineTree from './OutlineTree';
import {
    applyToOutlineNode,
    makeChartSectionUpdate,
    makeDataKeywordUpdate,
    makeExtraToggle,
} from './outlineUtils';
import PremiumOutlineTree from './PremiumOutlineTree';
import styles from './index.module.scss';

const LOADING_STEPS = [
    { key: 'analyze', icon: <FileSearchOutlined />, text: '正在分析研究主题...' },
    { key: 'structure', icon: <ApartmentOutlined />, text: '智能规划文章结构...' },
    { key: 'generate', icon: <HighlightOutlined />, text: '生成章节大纲...' },
    { key: 'optimize', icon: <BulbOutlined />, text: '优化大纲逻辑...' },
];

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const sectionIdsWithSubs = (outline) =>
    (outline || []).flatMap((ch) =>
        (ch.sections || []).filter((sec) => sec.subsections?.length > 0).map((sec) => sec.id),
    );

const CLOSED_MODAL = { visible: false, type: '', level: 1, data: null };

// 大纲生成加载动画：进度定时器内聚在子组件中，避免整步重渲染
const OutlineLoading = memo(function OutlineLoading() {
    const { token } = theme.useToken();
    const [progress, setProgress] = useState(0);
    const [stepIdx, setStepIdx] = useState(0);

    useEffect(() => {
        let p = 0;
        const timer = setInterval(() => {
            // 每 400ms 增加约 1.3，约 28 秒到 90% 后停住，等接口返回
            p += Math.random() * 0.6 + 1.0;
            if (p >= 90) {
                p = 90;
                clearInterval(timer);
            }
            setProgress(Math.min(Math.round(p), 90));
            setStepIdx(
                Math.min(
                    Math.floor(p / (90 / LOADING_STEPS.length)),
                    LOADING_STEPS.length - 1,
                ),
            );
        }, 400);
        return () => clearInterval(timer);
    }, []);

    const currentStep = LOADING_STEPS[stepIdx] || LOADING_STEPS[0];
    return (
        <div className={styles.circleLoadingWrapper} role="status" aria-live="polite">
            <div className={styles.circleLoadingMain}>
                <Progress
                    type="circle"
                    percent={progress}
                    size={140}
                    strokeColor={{ '0%': token.colorPrimary, '100%': '#6d5bf5' }}
                    trailColor="#f0f0f0"
                    format={() => (
                        <div className={styles.circleLoadingInner}>
                            <span className={styles.circleLoadingIcon}>{currentStep.icon}</span>
                            <span className={styles.circleLoadingPercent}>{progress}%</span>
                        </div>
                    )}
                />
            </div>
            <div className={styles.circleLoadingInfo}>
                <p className={styles.circleLoadingTitle}>{currentStep.text}</p>
                <div className={styles.circleLoadingSteps}>
                    {LOADING_STEPS.map((step, idx) => (
                        <span
                            key={step.key}
                            className={classNames(styles.circleLoadingStepItem, {
                                [styles.active]: idx === stepIdx,
                                [styles.done]: idx < stepIdx,
                            })}
                        >
                            {idx < stepIdx ? <CheckCircleFilled /> : step.icon}
                            {step.text.replace('...', '')}
                        </span>
                    ))}
                </div>
                <div className={styles.circleLoadingDots}>
                    <span className={styles.dot} />
                    <span className={styles.dot} />
                    <span className={styles.dot} />
                </div>
            </div>
        </div>
    );
});

// 下单时自动补充外文文献的结果；接口没返回篇数时不提示
const notifyEnglishSupplement = (count, selectedCount) => {
    if (typeof count !== 'number') return;
    if (count > 0) {
        message.success(`已自动补充 ${count} 篇外文文献，写作时会一并引用`);
    } else if (selectedCount >= MAX_REFERENCE_COUNT) {
        message.info(`参考资料已满 ${MAX_REFERENCE_COUNT} 条，本次没有补充外文文献`);
    } else {
        message.info('没有检索到与标题切题的外文文献，本次未补充');
    }
};

/**
 * 第二步：确认大纲。
 * 生成（普通引擎逐章揭示 / 特调一次展示）→ 本地编辑（增删改、插入图表、图类型、数据关键词）→ 创建订单。
 */
export default function OutlineReviewStep({
    title,
    queryType,
    model,
    onModelChange,
    wordCount,
    contents,
    enableLevel3,
    references,
    recommendEnglishLiterature = false,
    // 与当前生成参数匹配的已有大纲（从后面步骤退回、或参数未变时复用）
    cachedOutline,
    onOutlineChange,
    // 已创建的订单 { quote, sig }：下单参数完全一致时不再重复创建
    existingOrder,
    onComplete,
    onPrev,
}) {
    const isPremium = model === MODEL.ACADEMIC;
    const TreeView = isPremium ? PremiumOutlineTree : OutlineTree;

    const [outlineData, setOutlineData] = useState(() => cachedOutline || null);
    const [loading, setLoading] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const [streamingChapterIdx, setStreamingChapterIdx] = useState(-1);
    const [outlineError, setOutlineError] = useState(null);
    const [expandedChapters, setExpandedChapters] = useState(
        () => new Set((cachedOutline || []).map((ch) => ch.id)),
    );
    const [expandedSections, setExpandedSections] = useState(
        () => new Set(sectionIdsWithSubs(cachedOutline)),
    );
    const [allExpanded, setAllExpanded] = useState(true);
    const [chartPopoverKey, setChartPopoverKey] = useState(null);
    const [dataPopoverKey, setDataPopoverKey] = useState(null);
    const [editModal, setEditModal] = useState(CLOSED_MODAL);
    const [orderCreating, setOrderCreating] = useState(false);
    const outlineContainerRef = useRef(null);
    // 每次生成递增；卸载或重新生成时旧的请求 / 逐章动画自动作废
    const runIdRef = useRef(0);

    useEffect(
        () => () => {
            runIdRef.current += 1;
        },
        [],
    );

    // 手机上底栏吸底：给 body 加标记，右下角客服 / 公众号浮钮上移，不盖住「下一步」
    useEffect(() => {
        if (typeof window.matchMedia !== 'function') return undefined;
        if (!window.matchMedia('(max-width: 768px)').matches) return undefined;
        document.body.classList.add('lgfb-writing-footer');
        return () => document.body.classList.remove('lgfb-writing-footer');
    }, []);

    const outlineSummary = useMemo(
        () => ({
            chapters: outlineData?.length || 0,
            sections: (outlineData || []).reduce((sum, ch) => sum + (ch.sections?.length || 0), 0),
        }),
        [outlineData],
    );

    const generate = useCallback(async () => {
        const runId = ++runIdRef.current;
        const premium = model === MODEL.ACADEMIC;
        setLoading(true);
        setIsStreaming(false);
        setStreamingChapterIdx(-1);
        setOutlineData(null);
        setExpandedChapters(new Set());
        setExpandedSections(new Set());
        setAllExpanded(true);
        setOutlineError(null);
        setChartPopoverKey(null);
        setDataPopoverKey(null);
        onOutlineChange?.(null);

        let outlines;
        try {
            const data = await requestOutline({
                query: title,
                queryType,
                model,
                autoLightTypes: contentsToLightTypes(contents),
                wordCount,
                enableLevel3TableOfContents: Boolean(enableLevel3),
                references: (references || []).map((ref) => ref.id),
            });
            if (runIdRef.current !== runId) return;
            outlines = toEditableOutline(data?.outlines);
            if (!outlines.length) throw new Error('没有生成有效的大纲，请重新生成');
        } catch (err) {
            if (runIdRef.current !== runId) return;
            const text = errorText(err, '大纲生成失败，请重试');
            setOutlineError(text);
            if (!isErrorNotified(err)) message.error(text);
            setLoading(false);
            return;
        }

        if (premium) {
            // 特调接口已返回完整目录，直接展示
            setOutlineData(outlines);
            setExpandedChapters(new Set(outlines.map((ch) => ch.id)));
            setExpandedSections(new Set(sectionIdsWithSubs(outlines)));
            setLoading(false);
            return;
        }

        // 普通引擎：逐章揭示
        setIsStreaming(true);
        setOutlineData([]);
        for (let i = 0; i < outlines.length; i += 1) {
            setStreamingChapterIdx(i);
            await wait(200 + Math.random() * 200);
            if (runIdRef.current !== runId) return;
            const chapter = outlines[i];
            setOutlineData((prev) => [...(prev || []), chapter]);
            setExpandedChapters((prev) => new Set([...prev, chapter.id]));
            const secIds = sectionIdsWithSubs([chapter]);
            if (secIds.length) setExpandedSections((prev) => new Set([...prev, ...secIds]));
            await wait(50);
            if (runIdRef.current !== runId) return;
            outlineContainerRef.current
                ?.querySelector(`[data-chapter-idx="${i}"]`)
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setIsStreaming(false);
        setStreamingChapterIdx(-1);
        setLoading(false);
    }, [
        contents,
        enableLevel3,
        model,
        onOutlineChange,
        queryType,
        references,
        title,
        wordCount,
    ]);

    // 进入本步：没有可复用的大纲就生成（只在挂载时判断一次）
    const initialOutlineRef = useRef(cachedOutline);
    const generateRef = useRef(generate);
    generateRef.current = generate;
    useEffect(() => {
        if (!initialOutlineRef.current) generateRef.current();
    }, []);

    // 生成完成 / 编辑后同步给上层，退回需求配置再回来时可复用
    useEffect(() => {
        if (outlineData && !loading && !isStreaming) onOutlineChange?.(outlineData);
    }, [outlineData, loading, isStreaming, onOutlineChange]);

    // 在大纲页切换引擎：先改上层的 model，等本组件按新引擎重新渲染后再重新生成
    const [pendingEngine, setPendingEngine] = useState(null);
    useEffect(() => {
        if (!pendingEngine || pendingEngine !== model) return;
        setPendingEngine(null);
        generate();
    }, [pendingEngine, model, generate]);

    const switchToPremiumEngine = useCallback(() => {
        onModelChange?.(MODEL.ACADEMIC);
        setPendingEngine(MODEL.ACADEMIC);
        message.success('已切换为超级自研学术特调模型，正在重新生成大纲');
    }, [onModelChange]);

    // ---------- 展开 / 折叠 ----------
    const toggleChapter = useCallback((chapterId) => {
        setExpandedChapters((prev) => {
            const next = new Set(prev);
            if (next.has(chapterId)) next.delete(chapterId);
            else next.add(chapterId);
            return next;
        });
    }, []);

    const toggleSection = useCallback((sectionId) => {
        setExpandedSections((prev) => {
            const next = new Set(prev);
            if (next.has(sectionId)) next.delete(sectionId);
            else next.add(sectionId);
            return next;
        });
    }, []);

    const toggleAllExpand = useCallback(() => {
        if (allExpanded) {
            setExpandedChapters(new Set());
            setExpandedSections(new Set());
        } else {
            setExpandedChapters(new Set((outlineData || []).map((ch) => ch.id)));
            setExpandedSections(new Set(sectionIdsWithSubs(outlineData)));
        }
        setAllExpanded(!allExpanded);
    }, [allExpanded, outlineData]);

    // ---------- 增删改 ----------
    const handleEditChapter = useCallback(
        (chapter, isSection = false, parentId = null, level = isSection ? 2 : 1, grandParentId = null) => {
            setEditModal({
                visible: true,
                type: 'edit',
                isSection,
                parentId,
                grandParentId,
                level,
                data: {
                    id: chapter.id,
                    chapter: chapter.chapter || '',
                    chapterAbstract: chapter.chapterAbstract || '',
                },
            });
        },
        [],
    );

    // level: 1=章, 2=节, 3=小节
    const handleAddChapter = useCallback(
        (afterId, isSection = false, parentId = null, level = isSection ? 2 : 1, grandParentId = null) => {
            setEditModal({
                visible: true,
                type: 'add',
                isSection,
                parentId,
                grandParentId,
                afterId,
                level,
                data: { chapter: '', chapterAbstract: '' },
            });
        },
        [],
    );

    const handleDeleteChapter = useCallback(
        (nodeId, isSection = false, parentId = null, level = isSection ? 2 : 1, grandParentId = null) => {
            const levelName = level === 3 ? '子节' : level === 2 ? '小节' : '章节';
            Modal.confirm({
                title: '确认删除',
                content: `确定要删除该${levelName}吗？`,
                okText: '删除',
                okType: 'danger',
                cancelText: '取消',
                onOk: () => {
                    setOutlineData((prev) => {
                        if (level === 3 && grandParentId && parentId) {
                            return prev.map((ch) =>
                                ch.id !== grandParentId
                                    ? ch
                                    : {
                                          ...ch,
                                          sections: (ch.sections || []).map((sec) =>
                                              sec.id !== parentId
                                                  ? sec
                                                  : {
                                                        ...sec,
                                                        subsections: (sec.subsections || []).filter(
                                                            (sub) => sub.id !== nodeId,
                                                        ),
                                                    },
                                          ),
                                      },
                            );
                        }
                        if (isSection && parentId) {
                            return prev.map((ch) =>
                                ch.id !== parentId
                                    ? ch
                                    : {
                                          ...ch,
                                          sections: (ch.sections || []).filter(
                                              (sec) => sec.id !== nodeId,
                                          ),
                                      },
                            );
                        }
                        return prev.filter((ch) => ch.id !== nodeId);
                    });
                    message.success('删除成功');
                },
            });
        },
        [],
    );

    const handleSaveEdit = useCallback(() => {
        const { type, isSection, parentId, grandParentId, afterId, level, data } = editModal;
        const chapterTitle = (data?.chapter || '').trim();
        if (!chapterTitle) {
            message.error('请输入章节标题');
            return;
        }
        const chapterAbstract = (data?.chapterAbstract || '').trim();

        if (type === 'edit') {
            const rename = (node) =>
                node.id === data.id ? { ...node, chapter: chapterTitle, chapterAbstract } : node;
            setOutlineData((prev) =>
                level === 3 && grandParentId && parentId
                    ? applyToOutlineNode(prev, rename, parentId, grandParentId)
                    : isSection && parentId
                      ? applyToOutlineNode(prev, rename, parentId, null)
                      : applyToOutlineNode(prev, rename, null, null),
            );
        } else if (type === 'add') {
            const newId = `new_${Date.now()}`;
            const insertAfter = (list, node) => {
                const items = [...(list || [])];
                const idx = items.findIndex((item) => item.id === afterId);
                items.splice(idx + 1, 0, node);
                return items;
            };
            const base = { id: newId, chapter: chapterTitle, chapterAbstract, extras: [], selected_extra: '' };

            if (level === 3 && grandParentId && parentId) {
                setOutlineData((prev) =>
                    prev.map((ch) =>
                        ch.id !== grandParentId
                            ? ch
                            : {
                                  ...ch,
                                  sections: (ch.sections || []).map((sec) =>
                                      sec.id !== parentId
                                          ? sec
                                          : { ...sec, subsections: insertAfter(sec.subsections, base) },
                                  ),
                              },
                    ),
                );
                setExpandedSections((prev) => new Set([...prev, parentId]));
            } else if (isSection && parentId) {
                setOutlineData((prev) =>
                    prev.map((ch) =>
                        ch.id !== parentId
                            ? ch
                            : { ...ch, sections: insertAfter(ch.sections, { ...base, subsections: [] }) },
                    ),
                );
                setExpandedChapters((prev) => new Set([...prev, parentId]));
            } else {
                setOutlineData((prev) => insertAfter(prev, { ...base, sections: [] }));
            }
        }

        setEditModal(CLOSED_MODAL);
        message.success(type === 'edit' ? '修改成功' : '添加成功');
    }, [editModal]);

    // ---------- 插入内容 ----------
    const toggleExtraContent = useCallback((sectionId, parentId, type, grandParentId = null) => {
        setOutlineData((prev) =>
            applyToOutlineNode(prev, makeExtraToggle(sectionId, type), parentId, grandParentId),
        );
    }, []);

    const updateSectionChart = useCallback((sectionId, parentId, chartUpdate, grandParentId = null) => {
        setOutlineData((prev) =>
            applyToOutlineNode(
                prev,
                makeChartSectionUpdate(sectionId, chartUpdate),
                parentId,
                grandParentId,
            ),
        );
    }, []);

    const updateSectionDataKeyword = useCallback(
        (sectionId, parentId, keyword, grandParentId = null) => {
            setOutlineData((prev) =>
                applyToOutlineNode(
                    prev,
                    makeDataKeywordUpdate(sectionId, keyword),
                    parentId,
                    grandParentId,
                ),
            );
        },
        [],
    );

    const extraButtonProps = {
        isPremium,
        chartPopoverKey,
        dataPopoverKey,
        setChartPopoverKey,
        setDataPopoverKey,
        toggleExtraContent,
        updateSectionChart,
        updateSectionDataKeyword,
    };

    // ---------- 下一步：创建订单后直接进入预览全文（预览页自动开始首节免费预览） ----------
    const handleComplete = useCallback(async () => {
        if (!outlineData?.length) {
            message.warning('请等待大纲生成完成');
            return;
        }
        const submitOutlines = toSubmitOutlines(outlineData);
        const customRef = buildCustomRef(references);
        const payload = {
            paperTitle: title,
            queryType,
            outlines: submitOutlines,
            autoLightTypes: dataSearchLightTypes(contentsToLightTypes(contents), submitOutlines),
            wordCount,
            model,
            enableLevel3: Boolean(enableLevel3),
            recommendEnglishLiterature: Boolean(recommendEnglishLiterature),
            ...(customRef && { customRef }),
        };
        const sig = JSON.stringify(payload);

        // 下单参数与已建订单完全一致（从预览返回未修改）：直接进入下一步
        if (existingOrder?.quote && existingOrder.sig === sig) {
            onComplete?.({ ...existingOrder, outlines: submitOutlines });
            return;
        }

        setOrderCreating(true);
        try {
            const quote = await createOrder(payload);
            if (!quote?.uid) throw new Error('创建订单失败，请重试');
            // 订单一建好就记到首页「我的生成记录」（待支付）；参数变了重建的订单替换掉上一张未付款的
            rememberOrder({
                no: quote.orderNo || quote.uid,
                uid: quote.uid,
                title,
                kind: 'WRITING',
                queryType,
                status: 'UNPAID',
                supersedes: existingOrder?.quote?.uid,
            });
            if (recommendEnglishLiterature) {
                notifyEnglishSupplement(quote.englishLiteratureCount, references?.length || 0);
            }
            onComplete?.({
                quote,
                sig,
                payload,
                outlines: submitOutlines,
                model: quote.model || model,
            });
        } catch (err) {
            if (!isErrorNotified(err)) message.error(errorText(err, '创建订单失败，请重试'));
            setOrderCreating(false);
        }
    }, [
        contents,
        enableLevel3,
        existingOrder,
        model,
        onComplete,
        outlineData,
        queryType,
        recommendEnglishLiterature,
        references,
        title,
        wordCount,
    ]);

    const busy = loading || orderCreating;

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.titleRow}>
                    <ProfileOutlined className={styles.titleIcon} />
                    <span className={styles.titleText}>生成文章大纲</span>
                </div>
                <div className={styles.hint}>
                    点击小节右侧的图标可快速添加图表、公式、代码等内容；章节支持新增、编辑、删除
                </div>
            </div>

            <div className={styles.body}>
                {outlineError ? (
                    <div className={styles.errorWrapper}>
                        <div className={styles.errorText}>{outlineError}</div>
                        <button type="button" className={styles.nextBtn} onClick={generate}>
                            重新生成
                        </button>
                    </div>
                ) : loading && !outlineData ? (
                    <OutlineLoading />
                ) : (
                    <>
                        {!isPremium && (
                            <EngineSwitchNotice disabled={busy} onSwitch={switchToPremiumEngine} />
                        )}
                        <TreeView
                            outlineData={outlineData}
                            isStreaming={isStreaming}
                            streamingChapterIdx={streamingChapterIdx}
                            expandedChapters={expandedChapters}
                            expandedSections={expandedSections}
                            allExpanded={allExpanded}
                            outlineContainerRef={outlineContainerRef}
                            generateOutline={generate}
                            regenerateDisabled={busy}
                            toggleChapter={toggleChapter}
                            toggleSection={toggleSection}
                            toggleAllExpand={toggleAllExpand}
                            handleEditChapter={handleEditChapter}
                            handleAddChapter={handleAddChapter}
                            handleDeleteChapter={handleDeleteChapter}
                            extraButtonProps={extraButtonProps}
                        />
                    </>
                )}
            </div>

            <div className={styles.footer}>
                <div className={styles.footerLeft}>
                    <button type="button" className={styles.backBtn} onClick={onPrev}>
                        上一步
                    </button>
                </div>
                <div className={styles.footerRight}>
                    {outlineSummary.chapters > 0 && !loading && (
                        <span className={styles.footerSummary}>
                            共 {outlineSummary.chapters} 章 · {outlineSummary.sections} 节
                        </span>
                    )}
                    <button
                        type="button"
                        className={styles.nextBtn}
                        onClick={handleComplete}
                        disabled={busy || !outlineData?.length}
                    >
                        {orderCreating
                            ? recommendEnglishLiterature
                                ? '正在补充外文文献，准备预览...'
                                : '正在准备预览全文...'
                            : '下一步：预览全文'}
                    </button>
                </div>
            </div>

            <EditChapterModal editModal={editModal} setEditModal={setEditModal} onOk={handleSaveEdit} />
        </div>
    );
}
